import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import api from "../../services/api";

const ProtectedRoute = () => {
  const location = useLocation();
  const token = localStorage.getItem("token");

  const isValidToken =
    token && token !== "undefined" && token !== "null" && token.trim() !== "";

  const currentPath = location?.pathname || "/app/home";
  const isProfileRoute = currentPath.startsWith("/app/profile");

  const clearSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("profile");
  };

  const parseNumber = (value) => {
    if (value === null || value === undefined) return NaN;

    const normalized = String(value).trim().replace(",", ".");
    if (!normalized) return NaN;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  };

  const readLocalProfile = () => {
    try {
      const raw = localStorage.getItem("profile");
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  };

  const isProfileComplete = (profile) => {
    if (!profile || typeof profile !== "object") return false;

    const name = String(
      profile.displayName ?? profile.display_name ?? profile.name ?? profile.nome ?? ""
    ).trim();
    const gender = String(profile.gender ?? profile.sexo ?? "").trim();
    const ageRange = String(
      profile.ageRange ?? profile.age_range ?? profile.faixaEtaria ?? ""
    ).trim();
    const height = parseNumber(
      profile.height ?? profile.heightCm ?? profile.height_cm ?? profile.altura
    );
    const startWeight = parseNumber(
      profile.startWeight ??
        profile.start_weight ??
        profile.initialWeight ??
        profile.pesoInicial
    );

    // Verifica se TODOS os campos da seção "Sobre Mim" foram preenchidos
    return Boolean(
      name !== "" &&
      gender !== "" &&
      ageRange !== "" &&
      Number.isFinite(height) && height > 0 &&
      Number.isFinite(startWeight) && startWeight > 0
    );
  };

  const [checkingProfile, setCheckingProfile] = useState(true);
  const [profileComplete, setProfileComplete] = useState(() => {
    const localProfile = readLocalProfile();
    return isProfileComplete(localProfile || {});
  });

  if (!isValidToken) {
    clearSession();

    try {
      const attemptedPath = currentPath || "/app/home";
      localStorage.setItem("postLoginRedirect", attemptedPath);
    } catch {
      // sem ação
    }

    try {
      localStorage.setItem(
        "authNotice",
        "Você precisa entrar para acessar essa área."
      );
    } catch {
      // sem ação
    }

    return (
      <Navigate
        to="/"
        replace
        state={{ reason: "auth_required", from: currentPath }}
      />
    );
  }

  useEffect(() => {
    const syncFromLocal = () => {
      const localProfile = readLocalProfile();
      setProfileComplete(isProfileComplete(localProfile || {}));
    };

    const handleProfileUpdated = () => syncFromLocal();
    const handleStorage = (event) => {
      if (event.key === "profile") {
        syncFromLocal();
      }
    };

    window.addEventListener("profileUpdated", handleProfileUpdated);
    window.addEventListener("storage", handleStorage);

    syncFromLocal();

    return () => {
      window.removeEventListener("profileUpdated", handleProfileUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      const localProfile = readLocalProfile();
      const localCompleteNow = isProfileComplete(localProfile || {});

      if (localCompleteNow) {
        if (alive) {
          setProfileComplete(true);
          setCheckingProfile(false);
        }
        return;
      }

      setCheckingProfile(true);

      try {
        const response = await api.get("/users/profile");
        const profile = response?.data?.profile || {};

        try {
          localStorage.setItem("profile", JSON.stringify(profile || {}));
          window.dispatchEvent(new Event("profileUpdated"));
        } catch {
          // sem ação
        }

        const complete = isProfileComplete(profile || {});
        if (alive) {
          setProfileComplete(complete);
        }
      } catch {
        if (alive) {
          const fallbackProfile = readLocalProfile();
          setProfileComplete(isProfileComplete(fallbackProfile || {}));
        }
      } finally {
        if (alive) {
          setCheckingProfile(false);
        }
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [token, currentPath]);

  if (checkingProfile && !profileComplete) {
    return (
      <div className="p-10 text-center" style={{ color: "var(--primary-deep)" }}>
        Carregando…
      </div>
    );
  }

  if (!profileComplete && !isProfileRoute) {
    return (
      <Navigate
        to="/app/profile"
        replace
        state={{ reason: "profile_incomplete", from: currentPath }}
      />
    );
  }

  return <Outlet />;
};

export default ProtectedRoute;